import React, { useContext } from "react"
import { Controller, useForm } from "react-hook-form"
import Link from "next/link"
import useSWR, { mutate } from "swr"

import { updateMyUserInfo } from "src/models/user/updateMyUserInfo"
import { ApiUserData } from "src/models/user/ApiUserData"
import DateSelector from "src/components/common/DateSelector"
import { useLoading } from "src/components/hooks/useLoading"
import { useTieupToast } from "src/components/hooks/useTieupToast"
import PageHeader from "src/components/common/layouts/PageHeader"
import HTMLHead from "src/components/utils/HTMLHead"
import routes from "routes"
import { globalStoreContext } from "src/store/GlobalStore"
import { UserData } from "types"
import { DefaultLayoutForReader } from "src/components/common/layouts/DefaultLayoutForReader"
import { usePermission } from "src/components/hooks/usePermission"
import { fetchMyUserInfo } from "../../models/user/fetchMyUserInfo"

const initialBirthdate = "2000-01-01"

const PrivacySettings: React.FC = () => {
  usePermission([2])

  const { handleSubmit, reset, control, setValue } = useForm<ApiUserData>()
  const { fullScreenLoading } = useLoading()
  const { addToastError, addToastInfo } = useTieupToast()

  const {
    globalStore: {
      user: { firebaseUserData },
    },
  } = useContext(globalStoreContext)

  const { data: myUserInfo } = useSWR(
    "/v1/mypage/user",
    async () => {
      const myUserInfo = await fetchMyUserInfo()
      reset(myUserInfo)

      return myUserInfo
    },
    {
      revalidateOnFocus: false,
    },
  )

  const onSubmit = async (values: UserData) => {
    try {
      fullScreenLoading(true)
      await updateMyUserInfo(values)
      addToastInfo("ユーザー情報を更新しました")
      mutate("/v1/mypage/user")
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      addToastError("ユーザー情報の更新に失敗しました")
    } finally {
      fullScreenLoading(false)
    }
  }

  if (!myUserInfo || !firebaseUserData) return null

  return (
    <DefaultLayoutForReader>
      <HTMLHead path="auth/privacy-settings" />
      <PageHeader title="プライバシー設定" hasBackButton={false} />
      <main>
        <div className="l-single -medium">
          <div className="privacySetting">
            <div className="privacySetting-inner">
              <div className="privacySetting-singIn">
                <h2 className="privacySetting-title">サインイン情報</h2>
                <div className="privacySetting-row">
                  <div className="privacySetting-label">サインイン方法</div>
                  {firebaseUserData.providers &&
                    firebaseUserData.providers[0] === "password" && (
                      <div className="privacySetting-text">
                        {firebaseUserData.email}
                        <div>
                          <Link href={routes.authChangeLoginEmail()}>
                            <a>編集</a>
                          </Link>
                        </div>
                      </div>
                    )}
                  {firebaseUserData.providers &&
                    firebaseUserData.providers[0] !== "password" && (
                      <div className="privacySetting-text">
                        {firebaseUserData.providers[0]}
                      </div>
                    )}
                </div>
              </div>

              <div className="privacySetting-user">
                <h2 className="privacySetting-title">ユーザー情報</h2>
                <div className="privacySetting-row">
                  <div className="privacySetting-label">ユーザーID</div>
                  <div className="privacySetting-text">
                    {myUserInfo.display_name}
                  </div>
                </div>
                <div className="privacySetting-row">
                  <div className="privacySetting-label">
                    通知用メールアドレス
                  </div>
                  <div className="privacySetting-text">
                    {myUserInfo.mail_address}
                  </div>
                </div>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="privacySetting-birth">
                    <div className="privacySetting-birth-label">
                      <label htmlFor="">生年月日変更</label>
                    </div>
                    <div style={{ width: "100%" }}>
                      <Controller
                        name="birthdate"
                        control={control}
                        defaultValue={myUserInfo.birthdate ?? initialBirthdate}
                        render={({ value }) => (
                          <DateSelector
                            selected={value ?? initialBirthdate}
                            onChange={(s: string) => {
                              setValue("birthdate", s)
                            }}
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="p-button">
                    <button className="c-button-secondary -full">登録</button>
                  </div>
                </form>
              </div>

              <ul className="privacySetting-link p-linkList">
                <li>
                  <Link href={routes.authChangeNotificationEmail()}>
                    <a className="p-linkList-anchor">
                      通知用メールアドレス変更
                    </a>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </DefaultLayoutForReader>
  )
}

export default PrivacySettings
